import React, { useState } from "react";
import { Compass, Target, Map, FileText, CheckCircle2, Send } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { api, formatApiError, SERVICE_AREAS } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const CAPABILITIES = [
  { title: "RTK GNSS Survey", desc: "Centimetre-level precision for boundary and plot layout." },
  { title: "DGPS Survey", desc: "Reliable positioning for larger agricultural and mapping projects." },
  { title: "Total Station", desc: "Dense topographic and feature mapping, ideal for construction prep." },
  { title: "Boundary & Demarcation", desc: "Clear physical demarcation with reference points." },
  { title: "Site Plan & AutoCAD Drafting", desc: "CAD-ready outputs for architects and planners." },
  { title: "Subdivision & Layout", desc: "Plot subdivision concepts aligned with field data." },
];

const PROCESS = [
  { step: "01", title: "Site Enquiry", desc: "Share location, mouza, dag number and approximate area." },
  { step: "02", title: "Scope & Quote", desc: "We finalise survey type, deliverables, timeline and price." },
  { step: "03", title: "Field Survey", desc: "Equipment deployment with trained operator and markers." },
  { step: "04", title: "Processing & Drafting", desc: "Data processing, drawing preparation and QA." },
  { step: "05", title: "Deliverables", desc: "Digital files, printed drawings and survey report." },
];

const FAQS = [
  { q: "What accuracy can I expect?", a: "RTK GNSS surveys typically deliver centimetre-level accuracy under good satellite conditions. Final accuracy depends on terrain, obstruction and reference setup." },
  { q: "Do you provide a government-certified survey?", a: "Statutory certification is issued by appropriate authorities. Our deliverables are technical outputs suitable for planning, demarcation and reference, and can be used alongside official records." },
  { q: "How long does a survey take?", a: "A typical plot survey takes a day on-site plus 2–4 days for drafting. Larger projects are scheduled based on scope." },
];

export default function LandSurvey() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", location: "", mouza: "", plot_dag_number: "", approximate_area: "", survey_type: "RTK GNSS", preferred_date: "", additional_details: "" });
  const [sub, setSub] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.location || !form.survey_type) {
      toast.error("Please fill in name, phone, location and survey type.");
      return;
    }
    setSub(true);
    try {
      await api.post("/survey-requests", form);
      toast.success("Survey request received. Our team will reach out shortly.");
      setForm({ ...form, additional_details: "", preferred_date: "", mouza: "", plot_dag_number: "", approximate_area: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSub(false);
    }
  };

  const input = "w-full bg-white border border-[#E2E8F0] rounded-lg px-3.5 py-2.5 text-sm text-[#1F2937] focus:outline-none focus:border-[#183B66]";

  return (
    <>
      <SEO
        title="Digital Land Survey & Mapping Services in Kolkata and South 24 Parganas"
        description="RTK GNSS, DGPS and Total Station land survey, boundary demarcation, site plan, AutoCAD drafting and subdivision mapping across Kolkata and South 24 Parganas."
        path="/land-survey"
        keywords="RTK GNSS survey Kolkata, DGPS land survey West Bengal, Total Station Kolkata, land surveyor Bawali, boundary survey South 24 Parganas, digital land mapping"
      />
      <section className="relative bg-[#101827] text-white overflow-hidden">
        <img src="https://images.unsplash.com/photo-1628158088791-89567a8e84ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxzdXJ2ZXlvciUyMHdpdGglMjB0b3RhbCUyMHN0YXRpb24lMjBsYW5kJTIwc3VydmV5fGVufDB8fHx8MTc5MTA0MzMzOXww&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#101827] via-[#101827]/85 to-[#101827]/30" />
        <div className="ae-container relative pt-28 pb-20">
          <div className="ae-eyebrow mb-3">Digital Land Survey</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] max-w-4xl">
            Precision Beyond Boundaries.
          </h1>
          <p className="text-white/80 mt-5 max-w-2xl leading-relaxed">
            RTK GNSS, DGPS and Total Station surveys with AutoCAD-ready outputs — for buyers, sellers,
            architects and landowners across Kolkata and South 24 Parganas.
          </p>
        </div>
      </section>

      <section className="ae-section">
        <div className="ae-container">
          <SectionHeader eyebrow="Capabilities" title="Our survey & mapping toolkit" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c, i) => (
              <div key={i} className="ae-card p-6">
                <div className="w-11 h-11 rounded-lg bg-[#183B66] text-white flex items-center justify-center mb-4"><Target className="w-5 h-5" /></div>
                <h3 className="font-semibold text-[#101827] text-lg mb-1.5">{c.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ae-section bg-[#F4F6F8]">
        <div className="ae-container">
          <SectionHeader eyebrow="Our Process" title="How a survey is delivered" />
          <div className="grid md:grid-cols-5 gap-5">
            {PROCESS.map((p) => (
              <div key={p.step}>
                <div className="text-5xl font-display font-semibold text-[#C5A46D]/30">{p.step}</div>
                <h3 className="font-semibold text-[#101827] text-lg mt-2 mb-1.5">{p.title}</h3>
                <p className="text-[#64748B] text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ae-section">
        <div className="ae-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <SectionHeader eyebrow="FAQ" title="Common questions about surveying" />
            <div className="space-y-3">
              {FAQS.map((f, i) => (
                <details key={i} className="ae-card p-5 group">
                  <summary className="list-none flex justify-between items-center cursor-pointer font-semibold text-[#101827]">
                    {f.q}
                    <span className="text-[#C5A46D] group-open:rotate-45 transition">+</span>
                  </summary>
                  <p className="mt-3 text-[#64748B] text-sm leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
          <aside className="lg:col-span-5">
            <form onSubmit={submit} className="ae-card p-6 sticky top-28 space-y-3" data-testid="survey-form">
              <div className="ae-eyebrow mb-1">Request a Site Survey</div>
              <h3 className="font-display text-2xl font-semibold text-[#101827] mb-2">Get a quotation</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={input} placeholder="Name *" value={form.name} onChange={update("name")} data-testid="survey-name" />
                <input className={input} placeholder="Phone *" value={form.phone} onChange={update("phone")} data-testid="survey-phone" />
              </div>
              <input className={input} placeholder="Email (optional)" value={form.email} onChange={update("email")} data-testid="survey-email" />
              <input className={input} placeholder="Location / Area *" value={form.location} onChange={update("location")} data-testid="survey-location" />
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={input} placeholder="Mouza (optional)" value={form.mouza} onChange={update("mouza")} />
                <input className={input} placeholder="Plot / Dag No. (optional)" value={form.plot_dag_number} onChange={update("plot_dag_number")} />
              </div>
              <input className={input} placeholder="Approximate area (e.g. 5 kottah)" value={form.approximate_area} onChange={update("approximate_area")} />
              <select className={input} value={form.survey_type} onChange={update("survey_type")} data-testid="survey-type">
                <option>RTK GNSS</option>
                <option>DGPS</option>
                <option>Total Station</option>
                <option>Boundary Survey</option>
                <option>Subdivision Planning</option>
                <option>Other</option>
              </select>
              <input type="date" className={input} value={form.preferred_date} onChange={update("preferred_date")} />
              <textarea className={input + " min-h-[80px]"} placeholder="Additional details" value={form.additional_details} onChange={update("additional_details")} />
              <button disabled={sub} className="ae-btn-primary w-full justify-center" data-testid="survey-submit">
                <Send className="w-4 h-4" /> {sub ? "Submitting..." : "Request Survey Quotation"}
              </button>
            </form>
          </aside>
        </div>
      </section>

      <section className="ae-section bg-[#F4F6F8]">
        <div className="ae-container">
          <SectionHeader eyebrow="Areas Served" title="Local to Kolkata & South 24 Parganas" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {SERVICE_AREAS.map((a) => (
              <div key={a} className="ae-card p-4 flex items-center gap-2 text-sm font-semibold text-[#101827]">
                <Map className="w-4 h-4 text-[#C5A46D]" /> {a}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
